import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  TableCellsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useMemo, useState } from "react"
import {
  type BudgetUpdateReportLine,
  useGetBudgetUpdateReportQuery,
} from "~/ui-rtk/api/budgetUpdateReportsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"
import {
  Button,
  DateRangePicker,
  ExportButton,
} from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import {
  exportTableToCSV,
  type SerializableColumnConfig,
} from "~/ui-rtk/utils/csv-export"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import HistoryChart from "./HistoryChart"
import PerformanceChart from "./PerformanceChart"

/** Quick ranges offered by the date picker (the widest is capped by the API's 400-day window). */
const QUICK_RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
]

type MetricFilter = "all" | "budget" | "bid-target"
type SourceFilter = "all" | "recommender" | "direct"
type SizeFilter = "all" | "big"
type View = "table" | "chart" | "performance"

const BID_TARGET_METRICS = new Set(["tROAS", "tCPA", "CPC ceiling"])

const isBidTarget = (line: BudgetUpdateReportLine) =>
  BID_TARGET_METRICS.has(line.metric)
const isBudget = (line: BudgetUpdateReportLine) =>
  !isBidTarget(line) && line.metric !== "Status"
const isRecommender = (line: BudgetUpdateReportLine) =>
  line.source === "Budget Recommender"
const isBig = (line: BudgetUpdateReportLine) => line.magnitude === "big"
/**
 * A row a recommender batch left at its current budget. It is in the ledger
 * so the run history shows the whole pool the batch allocated against, but
 * it changed nothing: never an "applied" change, never a KPI, never a step.
 */
const isLeftAsIs = (line: BudgetUpdateReportLine) => line.action === "skip"
const outcomeOf = (line: BudgetUpdateReportLine) =>
  isLeftAsIs(line) ? "left as is" : line.success ? "applied" : "failed"
/** The doctrine's red-flag shape: a big budget increase on branded search. */
const isBigBrandedIncrease = (line: BudgetUpdateReportLine) =>
  isBig(line) &&
  line.brandedSearch &&
  isBudget(line) &&
  line.deltaValue !== null &&
  line.deltaValue > 0

/**
 * What the evaluation doctrine says about a line — a finding to review, not
 * a verdict on the change. Empty for small moves.
 */
export const reviewNote = (line: BudgetUpdateReportLine): string => {
  if (isBigBrandedIncrease(line)) {
    return "Big increase on branded search — needs the incremental-click read (Δ spend ÷ Δ total branded clicks); platform ROAS is not evidence"
  }
  if (isBig(line)) {
    return "Big change (≥ 20% or ≥ $250/day) — must clear the total-spend / marginal-return gate"
  }
  return ""
}

/** Pill color per metric family: bid targets, status flips, budgets. */
const metricPillClass = (line: BudgetUpdateReportLine): string => {
  if (isBidTarget(line)) return "bg-violet-500/20 text-violet-200"
  if (line.metric === "Status") return "bg-amber-500/20 text-amber-200"
  return "bg-sky-500/20 text-sky-200"
}

const formatDelta = (delta: number | null) =>
  delta === null ? "" : `${delta > 0 ? "+" : ""}${delta}%`

/** CSV columns, in the table's order. */
export const HISTORY_CSV_COLUMNS: SerializableColumnConfig[] = [
  { key: "changedAt", header: "Changed At", type: "string" },
  { key: "changedBy", header: "Changed By", type: "string" },
  { key: "source", header: "Source", type: "string" },
  { key: "platform", header: "Channel", type: "string" },
  { key: "entity", header: "Entity", type: "string" },
  { key: "entityId", header: "Entity ID", type: "string" },
  { key: "entityName", header: "Entity Name", type: "string" },
  { key: "campaignName", header: "Campaign", type: "string" },
  { key: "metric", header: "Metric", type: "string" },
  { key: "previousLabel", header: "Previous", type: "string" },
  { key: "newLabel", header: "New", type: "string" },
  { key: "deltaLabel", header: "Change ($ and %)", type: "string" },
  { key: "deltaPercent", header: "Change %", type: "string" },
  { key: "magnitude", header: "Size", type: "string" },
  { key: "brandedSearch", header: "Branded search", type: "string" },
  { key: "review", header: "Review", type: "string" },
  { key: "outcome", header: "Outcome", type: "string" },
  { key: "error", header: "Error", type: "string" },
  { key: "initiativeType", header: "Initiative", type: "string" },
  { key: "notes", header: "Notes", type: "string" },
]

/** One CSV row per ledger line; dates in the viewer's local time, oldest first when sorted by the caller. */
export const historyCsvRow = (
  line: BudgetUpdateReportLine,
  formatDate: (iso: string) => string,
): Record<string, unknown> => ({
  changedAt: formatDate(line.changedAt),
  changedBy: line.changedBy,
  source: line.source,
  platform: line.platform,
  entity: line.entity,
  entityId: line.entityId,
  entityName: line.entityName,
  campaignName: line.campaignName,
  metric: line.metric,
  previousLabel: line.previousLabel,
  newLabel: line.newLabel,
  deltaLabel: line.deltaLabel,
  deltaPercent: formatDelta(line.deltaPercent),
  magnitude: line.magnitude ?? "",
  brandedSearch: line.brandedSearch ? "yes" : "",
  review: reviewNote(line),
  outcome: outcomeOf(line),
  error: line.error,
  initiativeType: line.initiativeType,
  notes: line.notes,
})

function FilterChips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div className="flex items-center gap-1">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-2 py-1 text-[12px] transition-colors",
            option.value === value
              ? "bg-system-blue-600 font-semibold text-white"
              : "bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function Kpi({
  label,
  value,
  alert,
}: {
  label: string
  value: number
  alert?: boolean
}) {
  return (
    <div className="flex flex-col rounded-lg border border-basic-blue bg-system-blue-800/60 px-3 py-2">
      <span className="text-[11px] uppercase tracking-wide text-system-gray-400">
        {label}
      </span>
      <span
        className={cn(
          "text-xl font-bold tabular-nums text-white",
          alert && value > 0 && "text-rose-300",
        )}
      >
        {value}
      </span>
    </div>
  )
}

const TH =
  "sticky top-0 z-10 border-b border-basic-blue bg-system-blue-800 px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-system-gray-400"
const TD =
  "border-b border-white/5 px-2 py-1 align-middle text-[12px] text-system-gray-100"

interface HistoryReportModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * "History report": the current company's log of every budget and tROAS
 * change recorded through Marathon (MD-5227) — Budget Recommender approves,
 * direct edits and Google preview-confirm changes — one line per changed
 * value with previous → new, for any date range picked with the shared
 * DateRangePicker. Exportable as CSV.
 */
export default function HistoryReportModal({
  isOpen,
  onClose,
}: HistoryReportModalProps) {
  const dayjs = useDate()
  // Day-granular range from the shared picker; the query spans whole days.
  const [range, setRange] = useState(() => ({
    from: dayjs().subtract(30, "day").startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  }))
  const [metricFilter, setMetricFilter] = useState<MetricFilter>("all")
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all")
  const [sizeFilter, setSizeFilter] = useState<SizeFilter>("all")
  const [view, setView] = useState<View>("table")
  const rangeValid = range.to > range.from

  const queryArgs = useMemo(
    () => ({ from: range.from.toISOString(), to: range.to.toISOString() }),
    [range],
  )
  const {
    data: report,
    isFetching,
    isError,
    refetch,
  } = useGetBudgetUpdateReportQuery(queryArgs, {
    skip: !isOpen || !rangeValid,
  })

  // The chart picks its own metric, so it only takes the source filter.
  const sourceLines = useMemo(() => {
    const all = report?.lines ?? []
    return all.filter(line => {
      if (sourceFilter === "recommender" && !isRecommender(line)) return false
      if (sourceFilter === "direct" && isRecommender(line)) return false
      return true
    })
  }, [report, sourceFilter])

  const lines = useMemo(
    () =>
      sourceLines.filter(line => {
        if (metricFilter === "budget" && !isBudget(line)) return false
        if (metricFilter === "bid-target" && !isBidTarget(line)) return false
        if (sizeFilter === "big" && !isBig(line)) return false
        return true
      }),
    [sourceLines, metricFilter, sizeFilter],
  )

  // Period summary above both views: only the source filter applies (it is
  // the one control visible in both), never the table-only metric chips —
  // otherwise "Budgets" would zero the tROAS tile and vice versa.
  // Rows a recommender batch left as is are in the ledger for the record,
  // not as changes: they count toward nothing here.
  const kpis = useMemo(() => {
    const changes = sourceLines.filter(l => !isLeftAsIs(l))
    return {
      budget: changes.filter(isBudget).length,
      bidTarget: changes.filter(isBidTarget).length,
      recommender: changes.filter(isRecommender).length,
      direct: changes.filter(l => !isRecommender(l)).length,
      big: changes.filter(isBig).length,
      bigBranded: changes.filter(isBigBrandedIncrease).length,
      failed: changes.filter(l => !l.success).length,
      leftAsIs: sourceLines.length - changes.length,
    }
  }, [sourceLines])

  const fmtDateTime = (d: Date | string) => dayjs(d).format("YYYY-MM-DD HH:mm")

  const handleExport = () => {
    const stamp = `${dayjs(range.from).format("YYYY-MM-DD")}_${dayjs(range.to).format("YYYY-MM-DD")}`
    // Oldest first — a change log reads top-down.
    const rows = [...lines]
      .sort((a, b) => a.changedAt.localeCompare(b.changedAt))
      .map(l => historyCsvRow(l, fmtDateTime))
    exportTableToCSV(rows, HISTORY_CSV_COLUMNS, {
      filename: `budget-history-${stamp}.csv`,
    })
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={1100}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-basic-blue px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-white">History report</h2>
          <p className="text-xs text-gray-400">
            Every budget and tROAS change recorded for this company — Budget
            Recommender approves, direct edits and Google preview-confirm
            changes — with previous → new values, the $ and % move, and the
            budget-evaluation flags: big changes (≥ 20% or ≥ $250/day) and
            branded-search campaigns, where platform ROAS is not evidence.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-system-gray-400 hover:bg-white/10 hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Toolbar: filters left, shared date picker + refresh right */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <div className="flex flex-wrap items-center gap-4">
          {/* Table ↔ chart. The table is the accessible readout of the chart. */}
          <div className="flex items-center gap-1 rounded-md bg-white/5 p-0.5">
            {(
              [
                { value: "table", label: "Table", Icon: TableCellsIcon },
                { value: "chart", label: "Changes", Icon: ChartBarIcon },
                {
                  value: "performance",
                  label: "Performance",
                  Icon: ArrowTrendingUpIcon,
                },
              ] as const
            ).map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setView(option.value)}
                className={cn(
                  "flex items-center gap-1 rounded px-2 py-1 text-[12px] transition-colors",
                  view === option.value
                    ? "bg-system-blue-600 font-semibold text-white"
                    : "text-system-gray-400 hover:text-system-gray-200",
                )}
              >
                <option.Icon className="h-3.5 w-3.5" />
                {option.label}
              </button>
            ))}
          </div>
          {view === "table" && (
            <FilterChips<MetricFilter>
              value={metricFilter}
              onChange={setMetricFilter}
              options={[
                { value: "all", label: "All metrics" },
                { value: "budget", label: "Budgets" },
                { value: "bid-target", label: "tROAS / bid targets" },
              ]}
            />
          )}
          {view === "table" && (
            <FilterChips<SizeFilter>
              value={sizeFilter}
              onChange={setSizeFilter}
              options={[
                { value: "all", label: "All sizes" },
                { value: "big", label: "Big changes" },
              ]}
            />
          )}
          <FilterChips<SourceFilter>
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[
              { value: "all", label: "All sources" },
              { value: "recommender", label: "Budget Recommender" },
              { value: "direct", label: "Direct / Google" },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            start={range.from}
            end={range.to}
            comparisonEnabled={false}
            customPredefinedPeriods={QUICK_RANGES}
            onChange={({ start, end }) => {
              setRange({
                from: dayjs(start).startOf("day").toDate(),
                to: dayjs(end).endOf("day").toDate(),
              })
            }}
          />
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            type="button"
            className="flex shrink-0 whitespace-nowrap"
            disabled={!rangeValid || isFetching}
            onClick={() => refetch()}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="px-5">
        {!rangeValid && (
          <p className="mb-2 text-sm text-rose-300">
            The range end must be after its start.
          </p>
        )}
        {isError && (
          <p className="mb-2 text-sm text-rose-300">
            Couldn&apos;t load the history report.
          </p>
        )}
        {report?.truncated && (
          <p className="mb-2 text-sm text-amber-300">
            The window holds more changes than the report cap; the oldest were
            dropped. Narrow the range.
          </p>
        )}
        <div className="mb-3 grid grid-cols-7 gap-2">
          <Kpi label="Budget changes" value={kpis.budget} />
          <Kpi label="tROAS / bid targets" value={kpis.bidTarget} />
          <Kpi label="Via Recommender" value={kpis.recommender} />
          <Kpi label="Direct / Google" value={kpis.direct} />
          <Kpi label="Big changes" value={kpis.big} />
          <Kpi
            label="Big brand-search increases"
            value={kpis.bigBranded}
            alert
          />
          <Kpi label="Failed" value={kpis.failed} alert />
        </div>
      </div>

      {/* Chart — how each entity's value moved through the range */}
      {view === "chart" && (
        <div className="relative mx-5 mb-3 flex-1 overflow-auto">
          {isFetching && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-basic-black/60 text-white">
              <Loader size="large" />
            </div>
          )}
          {report && <HistoryChart lines={sourceLines} range={range} />}
        </div>
      )}

      {/* Performance — one entity's daily metrics around its changes (Cube) */}
      {view === "performance" && (
        <div className="relative mx-5 mb-3 flex-1 overflow-auto">
          {isFetching && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-basic-black/60 text-white">
              <Loader size="large" />
            </div>
          )}
          {report && <PerformanceChart lines={sourceLines} range={range} />}
        </div>
      )}

      {/* Ledger — scrolls inside the dialog, header pinned */}
      <div
        className={cn(
          "relative mx-5 mb-3 min-h-[200px] flex-1 overflow-auto rounded-lg border border-basic-blue",
          view !== "table" && "hidden",
        )}
      >
        {isFetching && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-basic-black/60 text-white">
            <Loader size="large" />
          </div>
        )}
        <table className="w-max min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {[
                "Changed At",
                "Changed By",
                "Source",
                "Channel",
                "Entity",
                "Campaign",
                "Metric",
                "Previous",
                "New",
                "Change",
                "Review",
                "Outcome",
                "Initiative",
                "Notes",
              ].map(header => (
                <th key={header} className={TH}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report && lines.length === 0 && (
              <tr>
                <td colSpan={14} className={cn(TD, "text-system-gray-500")}>
                  No budget or tROAS changes in this range.
                </td>
              </tr>
            )}
            {lines.map(line => (
              <tr
                key={line.id}
                className={cn(
                  "hover:bg-white/5",
                  !line.success && "bg-rose-500/10",
                )}
              >
                <td className={cn(TD, "whitespace-nowrap")}>
                  {fmtDateTime(line.changedAt)}
                </td>
                <td className={TD}>{line.changedBy}</td>
                <td className={cn(TD, "whitespace-nowrap")}>{line.source}</td>
                <td className={TD}>{line.platform}</td>
                <td className={TD}>
                  <div className="flex max-w-[16rem] flex-col">
                    <span className="truncate" title={line.entityName}>
                      {line.entityName || line.entityId}
                    </span>
                    <span className="text-[11px] text-system-gray-500">
                      {line.entity} · {line.entityId}
                    </span>
                  </div>
                </td>
                <td className={TD}>
                  <span
                    className="block max-w-[14rem] truncate"
                    title={line.campaignName}
                  >
                    {line.campaignName}
                  </span>
                </td>
                <td className={TD}>
                  <span
                    className={cn(
                      "inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium",
                      metricPillClass(line),
                    )}
                  >
                    {line.metric}
                  </span>
                </td>
                <td className={cn(TD, "text-right tabular-nums")}>
                  {line.previousLabel || "—"}
                </td>
                <td className={cn(TD, "text-right tabular-nums")}>
                  {line.newLabel || "—"}
                </td>
                <td
                  className={cn(
                    TD,
                    "whitespace-nowrap text-right tabular-nums",
                    line.deltaValue !== null &&
                      line.deltaValue > 0 &&
                      "text-emerald-300",
                    line.deltaValue !== null &&
                      line.deltaValue < 0 &&
                      "text-rose-300",
                  )}
                >
                  {line.deltaLabel || "—"}
                </td>
                <td className={TD}>
                  <div
                    className="flex flex-wrap items-center gap-1"
                    title={reviewNote(line) || undefined}
                  >
                    {isBig(line) && (
                      <span
                        className={cn(
                          "inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                          isBigBrandedIncrease(line)
                            ? "bg-rose-500/20 text-rose-200"
                            : "bg-amber-500/20 text-amber-200",
                        )}
                      >
                        Big
                      </span>
                    )}
                    {line.brandedSearch && (
                      <span className="inline-block whitespace-nowrap rounded bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200">
                        Brand search
                      </span>
                    )}
                  </div>
                </td>
                <td className={TD}>
                  {isLeftAsIs(line) ? (
                    <span className="text-system-gray-400">left as is</span>
                  ) : line.success ? (
                    <span className="text-emerald-300">applied</span>
                  ) : (
                    <span className="text-rose-300" title={line.error}>
                      failed{line.error ? ` · ${line.error}` : ""}
                    </span>
                  )}
                </td>
                <td className={TD}>{line.initiativeType}</td>
                <td className={cn(TD, "text-system-gray-400")}>
                  <span
                    className="block max-w-[20rem] truncate"
                    title={line.notes}
                  >
                    {line.notes}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 border-t border-basic-blue px-5 py-3">
        {/* Exports the table as filtered — so only where those filters are
            visible. The Changes chart carries its own "Export table". */}
        {view === "table" && (
          <ExportButton onExport={handleExport} className="mr-auto" />
        )}
        <Button
          variant={{ variant: "outlined" }}
          size="sm"
          type="button"
          className="ml-auto"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Dialog>
  )
}
